import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import useCollection from 'Hooks/useCollection';
import MetaCollectionFormDialog from 'Components/UI/Meta/MetaForm/MetaCollectionFormDialog';

import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import relationships from 'Consts/Relationships';
import { addQueryWithReplace, QueryVal } from 'Helpers/Query';
import { SimpleActionItemEditOverride, SimpleParaplannerActionItemEditOverride } from 'Resources/SimpleActionItem';
import { closeModalWithAdditionalKeys } from 'Helpers/ModalHelper';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import { advisorHouseholdUri } from 'Consts/advisorUris';
import Button from 'Components/UI/Button';
import { dispatchActionItemRefresh, dispatchThreadListRefresh } from 'Hooks/useRefreshEvent';
import { advisorMessagesPath } from 'Consts/Strings';
import { balanceSheetPageURL } from 'Hooks/useBalanceSheetPageURL';
import { navigateToUriInNewTab } from 'Helpers/Navigation';
import useUserContext from 'Context/UserContext';

// eslint-disable-next-line react/prop-types
function SimpleActionItemModal({ defaultValues }) {
  const { householdResource } = useSimpleHousehold();
  const { collection, setCollection } = useCollection();
  const { isAgent } = useUserContext();
  const location = useLocation();

  useEffect(() => {
    if (!householdResource?.relationship(relationships.simpleActionItems)) {
      return;
    }

    const loadActionItemCollection = async () => {
      const todoId = QueryVal('todoId');
      const params = todoId ? { id: todoId } : { limit: 0 };
      let override = SimpleActionItemEditOverride;
      if (defaultValues?.isParaplanner) {
        override = SimpleParaplannerActionItemEditOverride;
      }
      Object.keys(defaultValues).forEach((key) => {
        const defaultValue = defaultValues[key];
        if (key === 'assignedTo') {
          if (defaultValues[key] === 'Household') {
            let defaultOptionValue = '';
            override = override.nameOverride(key, (v) => {
              const options = v.options.filter(option => option.group === 'Household');
              if (options.length > 0) {
                defaultOptionValue = options[0].id.toString();
              }
              return { default: defaultOptionValue };
            });
          } else if (defaultValues[key] === 'Me') {
            let defaultOptionValue = '';
            override = override.nameOverride(key, (v) => {
              const options = v.options.filter(option => option.group === 'Me');
              if (options.length > 0) {
                defaultOptionValue = options[0].id.toString();
              }
              return { default: defaultOptionValue };
            });
          }
        } else {
          override = override.nameOverride(key, { default: defaultValue });
        }
      });
      const coll = await householdResource
        .relationship(relationships.simpleActionItems)
        ?.link('related')
        ?.Get({ params });
      if (todoId) {
        const collWithOverride = coll.overrideMeta(override);
        setCollection(collWithOverride);
      } else {
        setCollection(coll.create({
          metaOverrideSet: override,
          addToNewCollection: true,
        }));
      }
    };

    loadActionItemCollection();
  }, [householdResource]);

  if (!collection) {
    return null;
  }

  const createdTaskFromMessages = location.pathname.includes(advisorMessagesPath);

  const onSuccess = () => {
    displaySuccess('Todo Saved');
    if (createdTaskFromMessages) {
      dispatchThreadListRefresh();
    } else {
      addQueryWithReplace({ refresh: 'action-item' });
      dispatchActionItemRefresh();
    }
    closeModalWithAdditionalKeys('todoId');
  };

  const onDelete = () => {
    displaySuccess('ToDo Deleted');
    addQueryWithReplace({ refresh: 'action-item' });
    dispatchActionItemRefresh();
    closeModalWithAdditionalKeys('todoId');
  };

  const onFailure = (err) => {
    displayErrorMessage(err, err.errors);
  };

  let dialogRows = [
    ['name'],
    ['dueDate', 'priority'],
    ['status', 'timeToComplete'],
    ['assignedTo', 'showOnBlueprint'],
    ['description'],
  ];

  if (defaultValues?.isParaplanner) {
    dialogRows = [
      ['name'],
      ['dueDate', 'priority'],
      ['status'],
      ['assignedTo'],
      ['description'],
    ];
  }
  let householdText = '';
  if (householdResource) {
    householdText = `${householdResource.attr('primaryClientFirstName')} ${householdResource.attr('primaryClientLastName')} [${householdResource.id()}]`;
  }

  const additionalActions = [];
  if (collection?.resources().head().attr('householdId')) {
    additionalActions.push({
      component: (resource) => {
        return (
          <Link to={advisorHouseholdUri(resource.attr('householdId'))} target="_blank">
            <Button
              color="secondary"
              variant="contained"
            >
              {'View Client Page'}
            </Button>
          </Link>
        );
      },
    });
  }

  if (collection?.resources().head().relationship('account')) {
    additionalActions.push({
      component: (resource) => {
        const url = balanceSheetPageURL(resource, resource.attr('householdId'), isAgent);
        if (!url) {
          return null;
        }
        return (
          <Button
            color="secondary"
            variant="contained"
            onClick={(e) => navigateToUriInNewTab(e, url)}
          >
            Edit Account
          </Button>
        );
      },
    });
  }

  return (
    <MetaCollectionFormDialog
      open
      onClose={() => closeModalWithAdditionalKeys('todoId')}
      onSubmit={onSuccess}
      onFailure={onFailure}
      collection={collection}
      title={QueryVal('todoId') ? `Edit Task for ${householdText}` : `Add Task for ${householdText}`}
      rows={dialogRows}
      onDelete={collection.resources().head().id() ? onDelete : null}
      additionalActions={additionalActions}
      disableBackdropClickClosure
      test={'simple-action-item-dialog'}
      SubmitButtonProps={{
        test: 'simple-action-item-dialog-submit-button',
      }}
    />
  );
}

SimpleActionItemModal.defaultProps = {
  defaultValues: {},
};

SimpleActionItemModal.propTypes = {
  defaultValues: PropTypes.object,
};

export default SimpleActionItemModal;
